import React, { createContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { getAuthUserSelector } from '@sophocles/features/auth/authSelectors';
import {
  handleSiteRoomNotification,
  handleUserRoomNotification,
  joinUserRoom,
  leaveUserRoom,
  mainSocket,
  selectSiteRoomV2,
  selectSiteRoom,
  selectSocketId,
  selectUserRoom,
  setSocketId,
  siteSocket,
  userSocket,
} from '@sophocles/socket-io/slice';

const SocketContext = createContext();

export function SocketProvider({ children }) {
  const dispatch = useDispatch();
  const socketId = useSelector(selectSocketId);
  const siteRoom = useSelector(selectSiteRoom);
  const siteRoomV2 = useSelector(selectSiteRoomV2);
  const userRoom = useSelector(selectUserRoom);
  const user = useSelector(getAuthUserSelector);

  const [mainStatus, setMainStatus] = useState('disconnected');
  const [siteStatus, setSiteStatus] = useState('disconnected');
  const [userStatus, setUserStatus] = useState('disconnected');
  const [bulkStatus, setBulkStatus] = useState('disconnected');

  useEffect(() => {
    mainSocket.io.opts.reconnection = true;
    mainSocket.io.opts.reconnectionAttempts = Infinity;
    mainSocket.io.opts.reconnectionDelay = 1000;
    mainSocket.io.opts.reconnectionDelayMax = 5000;
    mainSocket.io.opts.timeout = 20000;

    mainSocket.on('connect', () => {
      setMainStatus('connected');
      mainSocket.emit('get_client_id');
    });
    mainSocket.on('disconnect', () => {
      setMainStatus('disconnected');
      console.log('SOCKET.IO DISCONNECT');
    });
    mainSocket.on('connect_error', () => setMainStatus('error'));
    mainSocket.io.on('reconnect_failed', () => setMainStatus('failed'));
    mainSocket.on('client_id', socket_id => dispatch(setSocketId(socket_id)));

    siteSocket.on('connect', () => {
      setSiteStatus('connected');
      if (siteRoom) siteSocket.emit('join', siteRoom);
      if (siteRoomV2) siteSocket.emit('join', siteRoomV2);
    });
    siteSocket.on('disconnect', () => setSiteStatus('disconnected'));
    siteSocket.on('connect_error', () => setSiteStatus('error'));
    siteSocket.io.on('reconnect_failed', () => setSiteStatus('failed'));
    siteSocket.on('notify', data => dispatch(handleSiteRoomNotification(data)));

    userSocket.on('connect', () => {
      setUserStatus('connected');
      if (userRoom) userSocket.emit('join', userRoom);
    });
    userSocket.on('disconnect', () => setUserStatus('disconnected'));
    userSocket.on('connect_error', () => setUserStatus('error'));
    userSocket.io.on('reconnect_failed', () => setUserStatus('failed'));
    userSocket.on('notify', data => dispatch(handleUserRoomNotification(data)));

    return () => {
      // Intentionally do NOT emit 'leave' here. The effect re-runs on
      // any dep change (siteRoom / siteRoomV2 / userRoom /
      // user object reference), and emitting leave on each
      // re-run kicks the socket out of the room it just joined. Room membership
      // is managed by the leaveSiteRoom / leaveSiteRoomV2 / leaveUserRoom
      // reducers (dispatched intentionally), and at the socket layer
      // the server removes a sid from all its rooms on disconnect.
      mainSocket.off('connect');
      mainSocket.off('client_id');
      mainSocket.off('disconnect');
      mainSocket.off('connect_error');
      mainSocket.io.off('reconnect_failed');
      siteSocket.off('connect');
      siteSocket.off('disconnect');
      siteSocket.off('connect_error');
      siteSocket.io.off('reconnect_failed');
      siteSocket.off('notify');
      userSocket.off('connect');
      userSocket.off('disconnect');
      userSocket.off('connect_error');
      userSocket.io.off('reconnect_failed');
      userSocket.off('notify');
    };
  }, [dispatch, siteRoom, siteRoomV2, userRoom, user]);

  useEffect(() => {
    const userId = user ? user.get('id', '0') : null;
    if (userId && userRoom !== userId) {
      dispatch(joinUserRoom(userId));
    } else if (!user && userRoom) {
      dispatch(leaveUserRoom(userRoom));
    }
  }, [dispatch, user, userRoom]);

  // Context value
  const value = {
    socketStatus: { mainStatus, siteStatus, userStatus, bulkStatus },
    socketId,
    siteRoom,
    siteRoomV2,
    userRoom,
    mainSocket,
    siteSocket,
    userSocket,
    dispatch,
  };

  return <SocketContext.Provider value={value}>{children}</SocketContext.Provider>;
}
